import type {
  LabMarkerTVitalLabMarkerMetadataT as LabMarkerT,
  LabTestCollectionMethodT,
  LabTestTVitalLabTestMetadataTVitalLabMarkerMetadataT as LabTestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { ICD10CodeT } from '@/entities/labs';

/** Step IDs retained by the legacy configured/custom-test workflow. */
export type LabOrderWizardStepId =
  | 'select-test'
  | 'custom-test'
  | 'disease-codes'
  | 'review'
  | 'confirmation';

/** Step IDs used by the native grouped-order workflow. */
export type NativeLabOrderWizardStepId = 'select-test' | 'disease-codes' | 'review';

export type LabOrderDraftMode = 'existing_test' | 'custom_test';

export interface LabOrderDraftBase {
  notes: string;
  priority: boolean;
  aoeAnswersById: Record<string, string>;
  icdCodes: ICD10CodeT[];
}

export interface ExistingTestLabOrderDraft extends LabOrderDraftBase {
  mode: 'existing_test';
  labTestId: string;
  labTestName?: string;
  /** Cached lab test object for AOE rendering and review. */
  selectedLabTest?: LabTestT;
}

export interface CustomTestLabOrderDraft extends LabOrderDraftBase {
  mode: 'custom_test';
  name: string;
  description: string;
  method: LabTestCollectionMethodT;
  selectedMarkers: LabMarkerT[];
}

export interface NativeOrderSetLabOrderDraft extends LabOrderDraftBase {
  labSlug: 'quest';
  method: LabTestCollectionMethodT;
  labTestIds: string[];
  markerIds: string[];
}

export type LabOrderDraft = ExistingTestLabOrderDraft | CustomTestLabOrderDraft;

/** Initial draft state for existing test mode. */
export const INITIAL_EXISTING_TEST_DRAFT: ExistingTestLabOrderDraft = {
  mode: 'existing_test',
  labTestId: '',
  labTestName: undefined,
  selectedLabTest: undefined,
  notes: '',
  priority: false,
  aoeAnswersById: {},
  icdCodes: [],
};

/** Initial draft state for custom test mode. */
export const INITIAL_CUSTOM_TEST_DRAFT: CustomTestLabOrderDraft = {
  mode: 'custom_test',
  name: '',
  description: '',
  method: 'walk_in_test',
  selectedMarkers: [],
  notes: '',
  priority: false,
  aoeAnswersById: {},
  icdCodes: [],
};

/** Initial draft state for one native Quest order. */
export const INITIAL_MARKER_ADD_ON_DRAFT: NativeOrderSetLabOrderDraft = {
  labSlug: 'quest',
  method: 'walk_in_test',
  labTestIds: [],
  markerIds: [],
  notes: '',
  priority: false,
  aoeAnswersById: {},
  icdCodes: [],
};
